/**
 * Shared validation utilities for the Baakas application
 * Consolidates duplicate validation functions across components
 */

// Security: Enhanced input validation
export function validateString(
  input: any, 
  fieldName: string, 
  minLength: number = 1, 
  maxLength: number = 100
): { valid: boolean; message?: string } {
  // First check if it's a string type
  if (typeof input !== 'string') {
    return { valid: false, message: `Invalid ${fieldName}: not a string` };
  }
  
  // If minLength is 0, allow empty strings; otherwise check if input is empty
  if (minLength > 0 && !input) {
    return { valid: false, message: `Invalid ${fieldName}: not a string` };
  }
  
  if (input.length < minLength || input.length > maxLength) {
    return { 
      valid: false, 
      message: `Invalid ${fieldName}: length must be between ${minLength} and ${maxLength} characters` 
    };
  }
  
  return { valid: true };
}

// Security: Enhanced number validation
export function validateNumber(
  value: any, 
  fieldName: string, 
  min?: number, 
  max?: number
): { valid: boolean; message?: string } {
  if (typeof value !== 'number' || isNaN(value)) {
    return { valid: false, message: `Invalid ${fieldName}: not a number` };
  }
  
  if (min !== undefined && value < min) {
    return { valid: false, message: `Invalid ${fieldName}: value must be >= ${min}` };
  }
  
  if (max !== undefined && value > max) {
    return { valid: false, message: `Invalid ${fieldName}: value must be <= ${max}` };
  }
  
  return { valid: true };
}

// Security: Enhanced array validation
export function validateArray(
  input: any, 
  fieldName: string, 
  minLength: number = 0, 
  maxLength: number = 1000
): { valid: boolean; message?: string } {
  if (!Array.isArray(input)) {
    return { valid: false, message: `Invalid ${fieldName}: not an array` };
  }
  
  if (input.length < minLength || input.length > maxLength) {
    return { 
      valid: false, 
      message: `Invalid ${fieldName}: length must be between ${minLength} and ${maxLength}` 
    };
  }
  
  return { valid: true };
}

// Security: Enhanced function validation
export function validateFunction(
  input: any, 
  fieldName: string
): { valid: boolean; message?: string } {
  if (typeof input !== 'function') {
    return { valid: false, message: `Invalid ${fieldName}: not a function` };
  }
  
  return { valid: true };
}

// Security: Enhanced email validation
export function validateEmail(email: string): { valid: boolean; message?: string } {
  if (!email || typeof email !== 'string') {
    return { valid: false, message: 'Email is required' };
  }
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Invalid email format' };
  }
  
  if (email.length > 254) {
    return { valid: false, message: 'Email address is too long' };
  }
  
  // Check for suspicious patterns
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return { valid: false, message: 'Invalid email format' };
  }
  
  return { valid: true };
}

// Security: Enhanced password validation
// Matches backend requirements: uppercase, lowercase, number, and special character
// SECURITY: Aligned with backend validation for consistency
export function validatePassword(password: string): { valid: boolean; message?: string } {
  if (!password || typeof password !== 'string') {
    return { valid: false, message: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long' };
  }
  
  if (password.length > 128) {
    return { valid: false, message: 'Password must be less than 128 characters' };
  }
  
  // Check for required character types (aligned with backend)
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  // SECURITY: Expanded special character set to match backend: @$!%*?&#^()_+-=[]{};':"|,.<>/
  const hasSpecial = /[@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/]/.test(password);
  
  if (!hasLowercase || !hasUppercase || !hasDigit || !hasSpecial) {
    const missing = [];
    if (!hasLowercase) missing.push('lowercase letter');
    if (!hasUppercase) missing.push('uppercase letter');
    if (!hasDigit) missing.push('number');
    if (!hasSpecial) missing.push('special character');
    return { 
      valid: false, 
      message: `Password must contain at least one ${missing.join(', ')}` 
    };
  }
  
  // Check for common weak passwords (expanded list to match backend)
  const commonPasswords = [
    'password', '12345678', 'qwerty', 'abc123', 'password123',
    'admin', 'letmein', 'welcome', 'monkey', '1234567890',
    'password1', 'qwerty123', 'admin123', 'welcome123'
  ];
  
  if (commonPasswords.some(weak => password.toLowerCase().includes(weak))) {
    return { valid: false, message: 'Password is too common. Please choose a stronger password.' };
  }
  
  // SECURITY: Check for repeated characters (e.g., "aaaaaa") - matches backend
  if (/(.)\1{3,}/.test(password)) {
    return { valid: false, message: 'Password contains too many repeated characters' };
  }
  
  // SECURITY: Check for sequential characters (e.g., "1234", "abcd") - matches backend
  if (/0123|1234|2345|3456|4567|5678|6789|7890|abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz/i.test(password)) {
    return { valid: false, message: 'Password contains sequential characters' };
  }
  
  return { valid: true };
}

// Security: Enhanced product validation
// Validates a single product object to ensure it has required fields
export function validateProduct(
  product: any,
  context: string = 'Product',
  index?: number
): { valid: boolean; message?: string } {
  try {
    if (!product || typeof product !== 'object') {
      return { 
        valid: false, 
        message: `${context}: Invalid product${index !== undefined ? ` at index ${index}` : ''}: not an object` 
      };
    }

    if (!product.id || typeof product.id !== 'string') {
      return { 
        valid: false, 
        message: `${context}: Invalid product ID${index !== undefined ? ` at index ${index}` : ''}: must be a string` 
      };
    }

    if (!product.title || typeof product.title !== 'string') {
      return { 
        valid: false, 
        message: `${context}: Invalid product title${index !== undefined ? ` at index ${index}` : ''}: must be a string` 
      };
    }

    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      message: `${context}: Error validating product${index !== undefined ? ` at index ${index}` : ''}: ${error}` 
    };
  }
}

// Filter and validate an array of products, returning only valid ones
export function filterValidProducts(
  products: any[],
  context: string = 'Products'
): any[] {
  return products.filter((product, index) => {
    const validation = validateProduct(product, context, index);
    if (!validation.valid) {
      // SECURITY: Removed console.warn to prevent information leakage
      return false;
    }
    return true;
  });
}

// Security: Validate Firestore document ID format
export function isValidDocumentId(id: string): boolean {
  if (!id || typeof id !== 'string') return false;
  if (id.length < 1 || id.length > 1500) return false;
  // Firestore document IDs can contain letters, numbers, underscores, and hyphens
  return /^[a-zA-Z0-9_-]+$/.test(id);
}

// Security: Validate product ID format
export function isValidProductId(id: string): boolean {
  return isValidDocumentId(id);
}